import {
	createRootRoute,
	createRoute,
	createRouter,
	Outlet,
	Link,
	lazyRouteComponent,
	redirect,
} from "@tanstack/react-router"
import { ProtectedLayout } from "@/components/app-shell"
import { Loading, Notice } from "@/components/common"

const rootRoute = createRootRoute({
	component: Outlet,
	errorComponent: ({ error, reset }) => (
		<main className="auth-page">
			<div className="panel max-w-lg p-8">
				<h1 className="mb-4 text-2xl font-semibold">Something needs another try</h1>
				<Notice error>{error instanceof Error ? error.message : "Please try again."}</Notice>
				<button className="mt-5 text-primary" onClick={reset}>
					Try again
				</button>
			</div>
		</main>
	),
	notFoundComponent: () => (
		<main className="auth-page">
			<div className="panel p-8">
				<h1 className="text-2xl font-semibold">Page not found</h1>
				<Link className="mt-5 block text-primary" to="/manage">
					Return to your workspace
				</Link>
			</div>
		</main>
	),
})
const appRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/manage",
	component: ProtectedLayout,
})
const routeTree = rootRoute.addChildren([
	createRoute({
		getParentRoute: () => rootRoute,
		path: "/",
		beforeLoad: () => {
			throw redirect({ href: "https://sumly.cy", reloadDocument: true, statusCode: 301 })
		},
	}),
	createRoute({
		getParentRoute: () => rootRoute,
		path: "/preferences/$token",
		component: lazyRouteComponent(() => import("@/pages/preferences"), "PreferencesPage"),
	}),
	appRoute.addChildren([
		createRoute({
			getParentRoute: () => appRoute,
			path: "/",
			component: lazyRouteComponent(() => import("@/pages/overview"), "OverviewPage"),
		}),
		createRoute({
			getParentRoute: () => appRoute,
			path: "/businesses",
			component: lazyRouteComponent(() => import("@/pages/businesses"), "BusinessesPage"),
		}),
		createRoute({
			getParentRoute: () => appRoute,
			path: "/businesses/$id",
			component: lazyRouteComponent(() => import("@/pages/businesses"), "BusinessDetailPage"),
		}),
		createRoute({
			getParentRoute: () => appRoute,
			path: "/segments",
			component: lazyRouteComponent(() => import("@/pages/segments"), "SegmentsPage"),
		}),
		createRoute({
			getParentRoute: () => appRoute,
			path: "/subscriptions",
			component: lazyRouteComponent(() => import("@/pages/segments"), "SubscriptionsPage"),
		}),
		createRoute({
			getParentRoute: () => appRoute,
			path: "/campaigns",
			component: lazyRouteComponent(() => import("@/pages/mailings"), "CampaignsPage"),
		}),
		createRoute({
			getParentRoute: () => appRoute,
			path: "/reminders",
			component: lazyRouteComponent(() => import("@/pages/mailings"), "RemindersPage"),
		}),
		createRoute({
			getParentRoute: () => appRoute,
			path: "/flows",
			component: lazyRouteComponent(() => import("@/pages/mailings"), "FlowsPage"),
		}),
		createRoute({
			getParentRoute: () => appRoute,
			path: "/mailings/$id",
			component: lazyRouteComponent(() => import("@/pages/mailings"), "MailingDetailPage"),
		}),
		createRoute({
			getParentRoute: () => appRoute,
			path: "/insights",
			component: lazyRouteComponent(() => import("@/pages/overview"), "InsightsPage"),
		}),
		createRoute({
			getParentRoute: () => appRoute,
			path: "/delivery",
			component: lazyRouteComponent(() => import("@/pages/delivery"), "DeliveryPage"),
		}),
		createRoute({
			getParentRoute: () => appRoute,
			path: "/templates",
			component: lazyRouteComponent(() => import("@/pages/content"), "TemplatesPage"),
		}),
		createRoute({
			getParentRoute: () => appRoute,
			path: "/assets",
			component: lazyRouteComponent(() => import("@/pages/content"), "AssetsPage"),
		}),
		createRoute({
			getParentRoute: () => appRoute,
			path: "/settings",
			component: lazyRouteComponent(() => import("@/pages/settings"), "SettingsPage"),
		}),
	]),
])
export const router = createRouter({
	routeTree,
	defaultPreload: "intent",
	defaultPendingComponent: Loading,
	scrollRestoration: true,
	trailingSlash: "never",
})
declare module "@tanstack/react-router" {
	interface Register {
		router: typeof router
	}
}
